import React, { useState, useCallback } from "react";
import { RichTextEditor } from "./RichTextEditor";
import { HtmlSourceEditor } from "./HtmlSourceEditor";
import type {
  AutocompleteProvider,
  ToolbarConfig,
  EditorCallbacks,
  IndentStyle,
} from "../types";

import "../styles/html-editor.css";

export type HtmlEditorTab = "visual" | "source" | "preview";

export interface HtmlEditorProps {
  /** Initial template HTML content (with {{variable}} syntax) */
  initialContent?: string;
  /** Called when template HTML changes (from either Visual or Source tab) */
  onChange?: (templateHtml: string) => void;
  /** Which tab to show initially (default: 'visual') */
  defaultTab?: HtmlEditorTab;
  /** Additional CSS class for the outer container */
  className?: string;

  // Shared props (forwarded to both editors)
  /** Autocomplete provider for Handlebars tags */
  autocompleteProvider?: AutocompleteProvider;
  /** Show friendly descriptions instead of {{variable}} syntax (default: false) */
  showDescriptions?: boolean;
  /** When true, allows any Handlebars syntax without provider validation (default: false) */
  allowFreeformHandlebars?: boolean;
  /** Max height of the editor content area (e.g. "400px", "50vh") */
  maxHeight?: string;

  // Visual Editor (RichTextEditor) props
  /** Custom toolbar configuration for the visual editor */
  toolbar?: ToolbarConfig;
  /** Whether the visual editor is editable (default: true) */
  editable?: boolean;
  /** Callbacks for interactive elements (links, images) in the visual editor */
  callbacks?: EditorCallbacks;
  /** Predefined color palette for text color picker */
  colorPalette?: string[];
  /** Predefined highlight/background colors */
  highlightPalette?: string[];
  /** Placeholder text for the visual editor */
  visualPlaceholder?: string;

  // Source Editor (HtmlSourceEditor) props
  /** Whether the source editor is read-only (default: false) */
  readOnly?: boolean;
  /** Indentation style for the source editor: 'tab', '2space', or '4space' (default: 'tab') */
  indentStyle?: IndentStyle;
  /** Placeholder text for the source editor */
  sourcePlaceholder?: string;
}

export const HtmlEditor: React.FC<HtmlEditorProps> = ({
  initialContent = "",
  onChange,
  defaultTab = "visual",
  className,
  // Shared
  autocompleteProvider,
  showDescriptions = false,
  allowFreeformHandlebars = false,
  maxHeight,
  // Visual-specific
  toolbar,
  editable = true,
  callbacks,
  colorPalette,
  highlightPalette,
  visualPlaceholder = "Start typing...",
  // Source-specific
  readOnly = false,
  indentStyle,
  sourcePlaceholder = "Enter HTML...",
}) => {
  const [templateHtml, setTemplateHtml] = useState<string>(initialContent);
  const [activeTab, setActiveTab] = useState<HtmlEditorTab>(defaultTab);
  const [editorKey, setEditorKey] = useState(0);

  const handleTabChange = useCallback(
    (newTab: HtmlEditorTab) => {
      if (newTab === activeTab) return;

      // Re-key the visual editor when switching back to it
      if (newTab === "visual") {
        setEditorKey((k) => k + 1);
      }

      setActiveTab(newTab);
    },
    [activeTab],
  );

  const handleTemplateChange = useCallback(
    (html: string) => {
      setTemplateHtml(html);
      onChange?.(html);
    },
    [onChange],
  );

  const handleSourceChange = useCallback(
    (html: string) => {
      setTemplateHtml(html);
      onChange?.(html);
    },
    [onChange],
  );

  return (
    <div className={`rich-editor-html-editor ${className || ""}`}>
      <div className="rich-editor-html-editor__tabs">
        <button
          type="button"
          className={`rich-editor-html-editor__tab${activeTab === "visual" ? " rich-editor-html-editor__tab--active" : ""}`}
          onClick={() => handleTabChange("visual")}
        >
          Visual Editor
        </button>
        <button
          type="button"
          className={`rich-editor-html-editor__tab${activeTab === "source" ? " rich-editor-html-editor__tab--active" : ""}`}
          onClick={() => handleTabChange("source")}
        >
          Source
        </button>
      </div>

      <div className="rich-editor-html-editor__content">
        {activeTab === "visual" && (
          <RichTextEditor
            key={editorKey}
            initialContent={templateHtml}
            onTemplateChange={handleTemplateChange}
            autocompleteProvider={autocompleteProvider}
            placeholder={visualPlaceholder}
            toolbar={toolbar}
            editable={editable}
            showDescriptions={showDescriptions}
            callbacks={callbacks}
            colorPalette={colorPalette}
            highlightPalette={highlightPalette}
            allowFreeformHandlebars={allowFreeformHandlebars}
            maxHeight={maxHeight}
          />
        )}
        {activeTab === "source" && (
          <HtmlSourceEditor
            value={templateHtml}
            onChange={handleSourceChange}
            placeholder={sourcePlaceholder}
            readOnly={readOnly}
            autocompleteProvider={autocompleteProvider}
            showDescriptions={showDescriptions}
            allowFreeformHandlebars={allowFreeformHandlebars}
            indentStyle={indentStyle}
            maxHeight={maxHeight}
          />
        )}
        {/* {activeTab === 'preview' && (
          <div
            className="rich-editor-html-editor__preview"
            style={maxHeight ? { maxHeight, overflowY: 'auto' as const } : undefined}
          >
            <iframe
              className="rich-editor-html-editor__iframe"
              srcDoc={templateHtml}
              title="HTML Preview"
              sandbox="allow-same-origin"
              style={maxHeight ? { height: maxHeight } : undefined}
            />
          </div>
        )} */}
      </div>
    </div>
  );
};

export default HtmlEditor;
